import { useEffect, useState } from "react";
import {
  MessageCircle, Check, X, Search, Smartphone, ShieldCheck, Handshake, Quote,
  ArrowRight, ExternalLink, MapPin, Clock, FileText, Rocket,
  Briefcase, Wrench, TrendingUp, Star, Award, Zap,
  Droplets, Waves, Thermometer, Hammer, Palette, Lock, ChevronRight,
} from "lucide-react";
import {
  WA_NUMBER, WA_DISPLAY, PRICE_TL, ADDRESS, waLink, waMessages,
  portfolio, faqs, included, compareChecks,
} from "@/data/sectors/tesisatci";
import mascotImg from "@/assets/sectors/tesisatci/mascot.png";
import profilPortrait from "@/assets/sectors/tesisatci/profil-portrait.png";
import WaterDropsBackground from "@/components/sectors/tesisatci/WaterDropsBackground";

import HeroPriceCard from "@/components/sectors/tesisatci/HeroPriceCard";
import logoDijitalMustafa from "@/assets/sectors/tesisatci/logo-dijital-mustafa.png";
import heroPlumber from "@/assets/sectors/tesisatci/hero-plumber.png";
import heroPhoneMascot from "@/assets/sectors/tesisatci/hero-phone-mascot-v2.png";

import iconWhatsapp from "@/assets/sectors/tesisatci/icon-whatsapp.png";
import iconPalette from "@/assets/sectors/tesisatci/icon-palette.png";
import iconRocket from "@/assets/sectors/tesisatci/icon-rocket.png";
import iconGoogleG from "@/assets/sectors/tesisatci/icon-google-g.png";

const CANONICAL = "https://www.reklam-uzmani.com.tr/tesisatci-web-sitesi";
const TITLE = "Su Tesisatçılarına Özel Web Sitesi | 13.000 TL Her Şey Dahil";
const DESC = "Tesisat firmanız için güven veren, mobil öncelikli profesyonel web sitesi. 5 sayfa, domain, 1 yıl hosting, SSL ve kurulum dahil net 13.000 TL.";

// ---------- analytics ----------
const track = (event: string, data: Record<string, any> = {}) => {
  try {
    const dl = ((window as any).dataLayer ||= []);
    dl.push({ event, sector: "su_tesisatcisi", ...data });
  } catch {}
};
const getCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : undefined;
};
const newEventId = () => {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch {}
  return "evt_" + Date.now() + "_" + Math.random().toString(36).slice(2, 10);
};
const trackWA = (placement: string) => {
  track("whatsapp_click", { placement });
  const eventId = newEventId();
  try {
    const w = window as any;
    // Browser Pixel — pass eventID for deduplication with CAPI
    if (w.fbq) {
      w.fbq("track", "Contact", { placement }, { eventID: eventId });
      w.fbq("track", "Lead", {
        content_name: "WhatsApp Lead",
        lead_type: "whatsapp_click",
        page_type: "tesisatci_web_sitesi",
        value: 13000,
        currency: "TRY",
      });
    }
  } catch {}
  // Server-side Conversions API (fire-and-forget, keepalive so it survives navigation)
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/meta-capi`;
    fetch(url, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({
        eventName: "Contact",
        eventId,
        eventSourceUrl: typeof window !== "undefined" ? window.location.href : "",
        placement,
        fbp: getCookie("_fbp"),
        fbc: getCookie("_fbc"),
      }),
    }).catch(() => {});
  } catch {}
};

const setMeta = (selector: string, key: string, val: string, content: string) => {
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) { el = document.createElement("meta"); el.setAttribute(key, val); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const setLink = (rel: string, href: string) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

// ---------- JSON-LD ----------
const jsonLd = () => ([
  {
    "@context": "https://schema.org", "@type": "WebPage",
    name: TITLE, description: DESC, url: CANONICAL, inLanguage: "tr-TR",
  },
  {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://www.reklam-uzmani.com.tr/" },
      { "@type": "ListItem", position: 2, name: "Tesisatçı Web Sitesi", item: CANONICAL },
    ],
  },
  {
    "@context": "https://schema.org", "@type": "Service",
    name: "Tesisatçı Web Sitesi Paketi",
    serviceType: "Web tasarım",
    provider: { "@type": "Person", name: "Dijital Mustafa", areaServed: "TR" },
    areaServed: "TR",
    offers: {
      "@type": "Offer", price: PRICE_TL, priceCurrency: "TRY",
      availability: "https://schema.org/InStock", url: CANONICAL,
    },
  },
  {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map(f => ({
      "@type": "Question", name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
]);

// ---------- small primitives ----------
const WaButton = ({
  label, message, placement, className = "", size = "md",
}: { label: string; message: string; placement: string; className?: string; size?: "sm" | "md" | "lg" }) => {
  const sz = size === "lg" ? "px-6 py-4 text-base" : size === "sm" ? "px-4 py-2.5 text-sm" : "px-5 py-3 text-[15px]";
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackWA(placement)}
      className={`inline-flex items-center justify-center gap-2 rounded-2xl font-semibold text-white shadow-[0_10px_24px_-8px_rgba(37,211,102,0.55)] transition-transform active:scale-[.98] hover:brightness-105 ${sz} ${className}`}
      style={{ background: "linear-gradient(180deg,#2BD46E 0%,#1EAE54 100%)", minHeight: 48 }}
      aria-label={label}
    >
      <MessageCircle className="h-5 w-5" aria-hidden="true" />
      <span>{label}</span>
    </a>
  );
};

const Mascot = ({ className = "", size = 96, alt = "" }: { className?: string; size?: number; alt?: string }) => (
  <img src={mascotImg} alt={alt} width={size} height={size} loading="lazy" decoding="async" className={className} style={{ width: size, height: size }} />
);

// ---------- Page ----------
export default function TesisatciWebSitesi() {
  const [portfIdx, setPortfIdx] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    document.title = TITLE;
    document.documentElement.lang = "tr";
    setMeta('meta[name="description"]', "name", "description", DESC);
    setMeta('meta[name="robots"]', "name", "robots", "index,follow");
    setMeta('meta[property="og:title"]', "property", "og:title", TITLE);
    setMeta('meta[property="og:description"]', "property", "og:description", DESC);
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    setMeta('meta[property="og:url"]', "property", "og:url", CANONICAL);
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setLink("canonical", CANONICAL);
    track("view_sector_landing", { page_path: "/tesisatci-web-sitesi" });

    // Meta Pixel (only on this page)
    try {
      const w = window as any;
      if (!w.fbq) {
        w.fbq = function (...args: any[]) {
          w.fbq.callMethod ? w.fbq.callMethod.apply(w.fbq, args) : w.fbq.queue.push(args);
        };
        if (!w._fbq) w._fbq = w.fbq;
        w.fbq.push = w.fbq;
        w.fbq.loaded = true;
        w.fbq.version = "2.0";
        w.fbq.queue = [];
        const t = document.createElement("script");
        t.async = true;
        t.src = "https://connect.facebook.net/en_US/fbevents.js";
        const s = document.getElementsByTagName("script")[0];
        s.parentNode?.insertBefore(t, s);
        w.fbq("init", "1259029412866832");
        w.fbq("track", "PageView");
      }
    } catch {}
  }, []);

  return (
    <div className="ts-root relative min-h-screen antialiased" style={{ background: "var(--ts-bg)" }}>
      <noscript
        dangerouslySetInnerHTML={{
          __html:
            '<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=1259029412866832&ev=PageView&noscript=1" alt="" />',
        }}
      />
      <style>{`
        .ts-root {
          --ts-bg:#FBFDFD; --ts-surface:#FFFFFF; --ts-ice:#EFFBFF;
          --ts-blue:#149FE8; --ts-blue-dark:#087EC0; --ts-turq:#16C7B7;
          --ts-navy:#12324A; --ts-text:#15334B; --ts-text-2:#607789;
          --ts-yellow:#FFC857; --ts-coral:#FF7D67;
          --ts-wa:#25D366; --ts-wa-hover:#1EAE54;
          --ts-border:#D9EDF5; --ts-soft:#E7F3F8;
          color: var(--ts-text);
          font-family: system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
        }
        .ts-card { background: var(--ts-surface); border: 1px solid var(--ts-border); box-shadow: 0 8px 24px -12px rgba(20,159,232,0.18), 0 2px 6px -2px rgba(20,159,232,0.08); }
        .ts-pill { background: var(--ts-ice); border: 1px solid var(--ts-border); color: var(--ts-navy); }
        .ts-pipe { height: 8px; border-radius: 999px; background: linear-gradient(90deg,#5FE6D5,#16C7B7,#5FE6D5); box-shadow: inset 0 -2px 0 rgba(0,0,0,.06), 0 4px 10px -4px rgba(22,199,183,.4); }
        .no-scrollbar::-webkit-scrollbar{display:none} .no-scrollbar{-ms-overflow-style:none;scrollbar-width:none}
        @media (prefers-reduced-motion: reduce){ *{animation:none!important;transition:none!important} }
        .safe-bottom{padding-bottom: calc(0.75rem + env(safe-area-inset-bottom));}
      `}</style>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd()) }} />

      {/* Global animated water droplets background */}
      <WaterDropsBackground variant="page" />

      {/* ================= HEADER ================= */}
      <header className="sticky top-0 z-40 backdrop-blur bg-white/85 border-b border-[var(--ts-soft)]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={logoDijitalMustafa}
              alt="Dijital Mustafa"
              width={520}
              height={240}
              decoding="async"
              className="h-11 sm:h-12 w-auto"
            />
            <span
              className="relative inline-flex items-center gap-1.5 rounded-full px-3 py-[5px] text-[11px] sm:text-[12px] font-extrabold whitespace-nowrap overflow-hidden"
              style={{
                background: "linear-gradient(120deg, #0B3A8C 0%, #1E63E9 45%, #22C55E 100%)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.35)",
                boxShadow: "0 6px 18px -6px rgba(30,99,233,0.55), inset 0 0 0 1px rgba(255,255,255,0.12)",
                letterSpacing: "0.02em",
              }}
            >
              <span
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)",
                  transform: "translateX(-120%)",
                  animation: "ts-badge-shine 3.6s ease-in-out infinite",
                }}
              />
              <span
                aria-hidden="true"
                className="relative inline-block w-1.5 h-1.5 rounded-full bg-white"
                style={{ animation: "ts-badge-pulse 1.6s ease-in-out infinite", boxShadow: "0 0 0 0 rgba(255,255,255,0.9)" }}
              />
              <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" className="relative drop-shadow">
                <path d="M12 2s6 7.2 6 12a6 6 0 1 1-12 0C6 9.2 12 2 12 2z" fill="#fff" />
              </svg>
              <span className="relative">Sadece Tesisatçılara Özel</span>
              <span
                className="relative ml-1 hidden sm:inline-block rounded-full px-1.5 py-[1px] text-[10px] font-black"
                style={{ background: "#FFD400", color: "#0B3A8C", letterSpacing: "0.06em" }}
              >
                VIP
              </span>
            </span>
            <style>{`
              @keyframes ts-badge-shine {
                0% { transform: translateX(-120%); }
                55% { transform: translateX(120%); }
                100% { transform: translateX(120%); }
              }
              @keyframes ts-badge-pulse {
                0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.85); }
                50% { box-shadow: 0 0 0 6px rgba(255,255,255,0); }
              }
            `}</style>
          </div>
          <a
            href={waLink(waMessages.header)}
            target="_blank" rel="noopener noreferrer"
            onClick={() => trackWA("header")}
            aria-label="WhatsApp'tan yaz"
            className="inline-flex items-center justify-center rounded-full shadow-lg shrink-0"
            style={{ width: 44, height: 44 }}
          >
            <img src={iconWhatsapp} alt="" width={44} height={44} className="w-11 h-11" />
          </a>
        </div>
      </header>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(180deg,#F4FBFF 0%,#E4F4FC 55%,#D6EEFA 100%)" }}>
        <WaterDropsBackground variant="section" density="low" />

        <div className="relative z-10 mx-auto max-w-2xl px-4 sm:px-6 pt-5 pb-8 md:pb-12">

          {/* Eyebrow */}
          <div
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] sm:text-[12px] font-extrabold tracking-[0.14em] uppercase"
            style={{
              background: "linear-gradient(120deg,#0B3A8C 0%,#149FE8 55%,#16C7B7 100%)",
              color: "#fff",
              boxShadow: "0 6px 18px -6px rgba(20,159,232,0.45)",
            }}
          >
            <Droplets className="h-3.5 w-3.5" aria-hidden="true" />
            Su Tesisatçılarına Özel Web Sitesi
          </div>

          {/* H1 */}
          <h1 className="mt-3 text-[28px] sm:text-[42px] leading-[1.12] font-extrabold tracking-tight text-[var(--ts-navy)]">
            Tesisat Firmanız İçin Güven Veren <span style={{ color: "var(--ts-blue-dark)" }}>Profesyonel Web Sitesi</span>
          </h1>

          {/* Main description */}
          <p className="mt-3 text-[15.5px] sm:text-[17px] text-[var(--ts-text)] leading-snug">
            Müşterileriniz hizmetlerinizi, çalışma bölgelerinizi ve iletişim bilgilerinizi tek bakışta görsün; telefon veya WhatsApp üzerinden size kolayca ulaşsın.
          </p>

          {/* Originality note */}
          <div
            className="mt-3 rounded-xl px-3.5 py-2.5 text-[13px] sm:text-[13.5px] text-[var(--ts-navy)] leading-snug border-l-4"
            style={{ background: "#EFFBFF", borderColor: "var(--ts-blue)" }}
          >
            <strong className="font-extrabold">Hazır bir tema yada kopya tasarım kullanmıyorum, tamamen size özel olarak hazırlıyorum.</strong>&nbsp;
            Web sitenizi firma adınıza, hizmetlerinize, çalışma bölgelerinize ve kurumsal görünümünüze göre özel olarak hazırlıyorum.
          </div>

          {/* Price card */}
          <div className="mt-5">
            <HeroPriceCard />
          </div>

          {/* Plumber portrait + phone/mascot mockup — side by side */}
          <div className="mt-6 grid grid-cols-2 gap-2 sm:gap-4 items-end">
            <div className="relative h-[240px] sm:h-[320px]">
              <img
                src={heroPlumber}
                alt="Su tesisatçısı ustası"
                width={896}
                height={1200}
                {...({ fetchpriority: "high" } as any)}
                decoding="async"
                className="absolute inset-x-0 bottom-0 mx-auto h-full w-auto max-w-full object-contain object-bottom drop-shadow-[0_18px_22px_rgba(0,0,0,0.18)]"
              />
            </div>
            <div className="relative h-[240px] sm:h-[320px]">
              <img
                src={heroPhoneMascot}
                alt="Tesisatçı web sitesi telefon önizleme ve maskot"
                width={940}
                height={1160}
                loading="eager"
                decoding="async"
                className="absolute inset-x-0 bottom-0 mx-auto h-full w-auto max-w-full object-contain object-bottom drop-shadow-[0_18px_22px_rgba(0,0,0,0.18)]"
              />
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-4 flex flex-col gap-2.5">
            <a
              href={waLink(waMessages.hero)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWA("hero_primary")}
              className="inline-flex items-center justify-center gap-2 rounded-2xl font-bold text-white shadow-[0_10px_24px_-8px_rgba(37,211,102,0.55)] transition-transform active:scale-[.98] hover:brightness-105 text-[15.5px]"
              style={{ background: "linear-gradient(180deg,#2BD46E 0%,#1EAE54 100%)", minHeight: 54 }}
              aria-label="WhatsApp'tan Paket Bilgisi Al"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
              WhatsApp'tan Paket Bilgisi Al
            </a>
            <button
              type="button"
              onClick={() => {
                track("secondary_cta_click", { placement: "hero" });
                document.getElementById("referanslar")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="inline-flex items-center justify-center gap-2 rounded-2xl font-bold text-[var(--ts-navy)] bg-white border-2 transition-colors hover:bg-[var(--ts-ice)] text-[15px]"
              style={{ borderColor: "var(--ts-blue)", minHeight: 54 }}
            >
              Gerçek Tesisatçı Sitelerini İncele
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {/* Trust badges */}
          <ul className="mt-4 grid grid-cols-2 gap-2" aria-label="Paket avantajları">
            {[
              { icon: Palette, t: "Firmanıza Özel Tasarım" },
              { icon: Smartphone, t: "Mobil Öncelikli ve Hızlı" },
              { icon: Search, t: "Google Ads ve SEO Uyumlu Altyapı" },
              { icon: Lock, t: "SSL ve Kurulum Dahil" },
            ].map(({ icon: Icon, t }) => (
              <li
                key={t}
                className="flex items-center gap-2 rounded-xl px-2.5 py-2 bg-white border border-[var(--ts-border)] shadow-[0_4px_12px_-8px_rgba(20,159,232,0.35)]"
              >
                <span
                  className="grid place-items-center rounded-lg shrink-0"
                  style={{ width: 30, height: 30, background: "var(--ts-ice)", color: "var(--ts-blue-dark)" }}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="text-[12px] sm:text-[12.5px] font-bold text-[var(--ts-navy)] leading-tight">{t}</span>
              </li>
            ))}
          </ul>

          {/* Services block */}
          <div className="mt-8">
            <h2 className="text-[20px] sm:text-[26px] font-extrabold text-[var(--ts-navy)] leading-tight">
              En Önemli Tesisat Hizmetlerinizi Açıkça Anlatalım
            </h2>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5" aria-label="Öne çıkan tesisat hizmetleri">
              {[
                { icon: Droplets, t: "Su Kaçağı Tespiti" },
                { icon: Waves, t: "Tıkanıklık Açma" },
                { icon: Thermometer, t: "Petek Temizliği" },
                { icon: Wrench, t: "Su Tesisat Arıza Servisi" },
                { icon: Hammer, t: "Su Tesisat Tadilatı" },
              ].map(({ icon: Icon, t }, idx) => (
                <li
                  key={t}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 bg-white border border-[var(--ts-border)] shadow-[0_6px_16px_-10px_rgba(20,159,232,0.35)]"
                >
                  <span
                    className="grid place-items-center rounded-lg shrink-0 text-white"
                    style={{ width: 36, height: 36, background: "linear-gradient(135deg,var(--ts-blue),var(--ts-blue-dark))" }}
                  >
                    <Icon className="h-4.5 w-4.5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold tracking-wider text-[var(--ts-text-2)]">0{idx + 1}</div>
                    <h3 className="text-[14px] sm:text-[15px] font-extrabold text-[var(--ts-navy)] leading-tight">{t}</h3>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[13.5px] text-[var(--ts-text-2)] leading-snug">
              Web sitenizde en çok talep gören hizmetlerinizi doğru sırayla, anlaşılır başlıklarla ve müşterinin kolayca karar verebileceği bir yapıyla sunuyoruz.
            </p>
          </div>
        </div>
      </section>


      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10 md:py-16">
        <div className="text-center">
          <div className="inline-block text-[11px] font-extrabold tracking-[0.22em] uppercase text-[var(--ts-blue-dark)]">
            Karar Psikolojisi
          </div>
          <h2 className="mt-2 text-[26px] md:text-4xl font-extrabold text-[var(--ts-navy)] leading-tight">
            Tesisat Müşterisinin Nasıl Karar Verdiğini Biliyorum
          </h2>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-[minmax(0,340px)_1fr] gap-6 md:gap-10 items-start">
          {/* Profil kartı */}
          <div className="relative rounded-3xl p-[2px]"
               style={{ background: "linear-gradient(135deg, var(--ts-blue) 0%, #16C7B7 100%)" }}>
            <div className="rounded-[22px] bg-white overflow-hidden shadow-[0_20px_40px_-20px_rgba(20,159,232,0.35)]">
              <div className="w-full h-[220px] md:h-[280px] overflow-hidden bg-[var(--ts-blue-50,#EAF6FE)]">
                <img
                  src={profilPortrait}
                  alt="Dijital Mustafa portresi"
                  width={520}
                  height={640}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-4 md:p-5">
                <div className="text-[12px] font-bold tracking-[0.14em] uppercase text-[var(--ts-blue-dark)]">
                  Dijital Mustafa
                </div>
                <p className="mt-2 text-[13.5px] leading-relaxed text-[var(--ts-navy)]">
                  17 yıllık dijital reklam yönetimi deneyimimden biliyorum: tesisat müşterisinin aciliyeti, arama niyeti ve güven arayışı çok net.
                  Web siteniz bu süreci doğru yönlendirdiğinde güçlü bir satış temsilcinize dönüşür.
                </p>
              </div>
            </div>
          </div>

          {/* Adımlar — mobilde dikey timeline, md+ 2 kolon */}
          <ol className="relative space-y-3 md:grid md:grid-cols-2 md:gap-3 md:space-y-0">
            <span
              aria-hidden="true"
              className="md:hidden absolute left-[27px] top-3 bottom-3 w-[2px] rounded-full"
              style={{ background: "linear-gradient(180deg, var(--ts-blue) 0%, #16C7B7 100%)" }}
            />
            {[
              { n: "01", t: "Arar", d: "İhtiyacını Google'da arar, aciliyet ile hızlı çözüm ister.", icon: <Search className="h-4.5 w-4.5" />, wa: false },
              { n: "02", t: "İnceler", d: "Site tasarımını, hizmetleri ve iletişim bilgilerini kontrol eder.", icon: <Smartphone className="h-4.5 w-4.5" />, wa: false },
              { n: "03", t: "Güvenir", d: "Referanslar, hizmet alanı ve profesyonel görünüm güven verir.", icon: <ShieldCheck className="h-4.5 w-4.5" />, wa: false },
              { n: "04", t: "WhatsApp'tan Yazar", d: "Kolayca WhatsApp üzerinden iletişim kurar veya arar.", icon: <MessageCircle className="h-4.5 w-4.5" />, wa: true },
              { n: "05", t: "Hizmet Alır", d: "Hızlı ve güvenli hizmet alır, memnun kalır.", icon: <Handshake className="h-4.5 w-4.5" />, wa: false },
            ].map((s) => (
              <li
                key={s.n}
                className="relative bg-white rounded-2xl border border-[var(--ts-blue)]/15 shadow-[0_8px_20px_-12px_rgba(11,59,90,0.25)] p-3.5 pl-[62px] flex items-start gap-3"
              >
                <span
                  className="absolute left-3 top-3.5 w-[42px] h-[42px] rounded-full grid place-items-center text-white text-[13px] font-extrabold shadow-md"
                  style={{
                    background: s.wa
                      ? "var(--ts-wa)"
                      : "linear-gradient(135deg, var(--ts-blue) 0%, var(--ts-blue-dark) 100%)",
                  }}
                >
                  {s.n}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-[var(--ts-navy)]">
                    <span className="text-[var(--ts-blue-dark)]">{s.icon}</span>
                    <h3 className="text-[14.5px] font-extrabold">{s.t}</h3>
                  </div>
                  <p className="mt-1 text-[13px] leading-snug text-[var(--ts-text-2)]">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Alt kapanış çubuğu */}
        <div
          className="mt-6 mx-auto max-w-2xl rounded-2xl px-5 py-4 flex items-center gap-3 text-center justify-center"
          style={{ background: "linear-gradient(135deg, #E8F7FE 0%, #E6FBF5 100%)" }}
        >
          <Quote className="h-5 w-5 text-[var(--ts-blue-dark)] shrink-0" />
          <p className="text-[14.5px] md:text-[15.5px] font-bold text-[var(--ts-navy)] leading-snug">
            Doğru web sitesi, doğru müşteriyi getirir.
          </p>
        </div>
      </section>

      {/* ================= EXPERT BAND ================= */}
      <section className="px-4 sm:px-6 py-8 md:py-12">
        <div className="mx-auto max-w-6xl relative overflow-hidden rounded-[32px] p-6 md:p-10 text-white"
             style={{ background: "linear-gradient(135deg,#0FB5A3 0%,#149FE8 100%)" }}>
          <WaterDropsBackground variant="section" density="high" />
          <div className="relative z-10 grid md:grid-cols-2 gap-6 md:gap-10 items-center">
            <div>
              <h2 className="text-[26px] md:text-4xl font-extrabold leading-tight">
                Sizi ve Müşterinizi<br/>Anlayan Bir Uzman
              </h2>
              <div className="mt-3 text-2xl md:text-3xl font-extrabold">Dijital Mustafa</div>
              <div className="mt-1 text-white/85">2007'den beri dijital reklam ve müşteri davranışı deneyimi</div>
              <div className="mt-5 bg-white text-[var(--ts-navy)] rounded-2xl p-4 shadow-lg relative">
                <div className="absolute -top-2 left-6 w-4 h-4 bg-white rotate-45" />
                <p className="text-[14.5px] leading-relaxed">
                  Tesisat müşterisinin neye baktığını, neye güvendiğini ve ne zaman iletişime geçtiğini biliyorum.
                </p>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {["Reklam Uzmanı", "Dönüşüm Odaklı", "Tesisat Sektörüne Özel"].map(t => (
                  <span key={t} className="rounded-full px-3.5 py-1.5 text-[12.5px] font-bold bg-white/20 border border-white/30 backdrop-blur">{t}</span>
                ))}
              </div>
            </div>
            <div className="relative flex justify-center md:justify-end">
              <img
                src={profilPortrait}
                alt="Dijital Mustafa portresi"
                width={520} height={640}
                loading="lazy" decoding="async"
                className="w-[75%] max-w-[360px] md:max-w-none md:w-[92%] object-contain drop-shadow-[0_25px_35px_rgba(0,0,0,0.25)]"
              />
              <Mascot size={64} alt="" className="absolute bottom-2 left-2 md:left-6" />
            </div>
          </div>
        </div>
      </section>

      {/* ================= PORTFOLIO ================= */}
      <section id="referanslar" className="mx-auto max-w-6xl px-4 sm:px-6 py-10 md:py-16 scroll-mt-16">
        <h2 className="text-center text-[26px] md:text-4xl font-extrabold text-[var(--ts-navy)]">Gerçek Çalışmalarımı İnceleyin</h2>
        <p className="text-center mt-3 text-[var(--ts-text-2)]">Hazır tema değil. Her işletmeye kendi görünümü.</p>

        {/* Desktop grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {portfolio.map(p => <PortfolioCard key={p.name} p={p} />)}
        </div>

        {/* Mobile carousel */}
        <div className="md:hidden mt-6">
          <div
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-4 px-4 pb-2"
            onScroll={(e) => {
              const el = e.currentTarget;
              const idx = Math.round(el.scrollLeft / (el.clientWidth * 0.85));
              if (idx !== portfIdx) setPortfIdx(idx);
            }}
          >
            {portfolio.map(p => (
              <div key={p.name} className="snap-start shrink-0" style={{ width: "85%" }}>
                <PortfolioCard p={p} />
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-1.5 mt-3">
            {portfolio.map((_, i) => (
              <div key={i} className="h-1.5 rounded-full transition-all" style={{ width: i === portfIdx ? 22 : 8, background: i === portfIdx ? "var(--ts-blue)" : "var(--ts-border)" }} />
            ))}
          </div>
        </div>
      </section>

      {/* ================= COMPARISON ================= */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10 md:py-16">
        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          <div className="ts-card rounded-3xl p-6 relative" style={{ background: "#F5F7F9" }}>
            <div className="text-[13px] font-bold uppercase tracking-wide text-[var(--ts-text-2)]">Herkese aynı site</div>
            <div className="mt-3 h-40 rounded-2xl grid place-items-center border border-dashed border-slate-300 bg-white/70">
              <div className="text-slate-400 flex flex-col items-center gap-2">
                <div className="w-20 h-20 rounded-full bg-slate-200 grid place-items-center text-3xl">😕</div>
                <span className="text-xs">Genel şablon</span>
              </div>
            </div>
          </div>
          <div className="ts-card rounded-3xl p-6 relative" style={{ background: "linear-gradient(180deg,#EFFBFF 0%,#FFFFFF 100%)" }}>
            <div className="text-[13px] font-bold uppercase tracking-wide" style={{ color: "var(--ts-blue-dark)" }}>Firmanıza özel site</div>
            <div className="mt-3 h-40 rounded-2xl grid place-items-center relative overflow-hidden" style={{ background: "linear-gradient(135deg,#149FE8,#087EC0)" }}>
              <Mascot size={110} alt="" />
              <div className="absolute bottom-2 right-2 text-white/90 text-[11px] font-bold">TESİSAT</div>
            </div>
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-6 items-start">
          <div>
            <h2 className="text-[26px] md:text-4xl font-extrabold text-[var(--ts-navy)]">Hazır Tema Değil, Size Özel</h2>
            <p className="mt-3 text-[var(--ts-text-2)]">Başka bir firmanın yazılarını kopyalamıyoruz.</p>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {compareChecks.map(c => (
              <div key={c} className="ts-card rounded-2xl px-3.5 py-3 flex items-center gap-2 text-[14px] font-semibold text-[var(--ts-navy)]">
                <span className="grid place-items-center h-6 w-6 rounded-full text-white shrink-0" style={{ background: "var(--ts-turq)" }}>
                  <Check className="h-3.5 w-3.5" />
                </span>{c}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= BENEFITS AROUND PHONE ================= */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10 md:py-16">
        <h2 className="text-[26px] md:text-4xl font-extrabold text-[var(--ts-navy)] max-w-md">Web Siteniz Size <span style={{ color: "var(--ts-blue)" }}>Şunları Kazandırır</span></h2>
        <div className="mt-8 grid md:grid-cols-3 gap-4 items-center">
          <div className="grid gap-3 order-2 md:order-1">
            {[
              { icon: <Smartphone className="h-4 w-4" />, t: "Müşteri telefondan kolayca bulur" },
              { icon: <Wrench className="h-4 w-4" />, t: "Hizmetleri hemen görür" },
              { icon: <MapPin className="h-4 w-4" />, t: "Hizmet bölgesini anlar" },
            ].map(b => <BenefitCard key={b.t} {...b} />)}
          </div>
          <div className="order-1 md:order-2 flex justify-center">
            <PhoneMockup />
          </div>
          <div className="grid gap-3 order-3">
            {[
              { icon: <ShieldCheck className="h-4 w-4" />, t: "Güven veren görünüm" },
              { icon: <MessageCircle className="h-4 w-4" />, t: "WhatsApp'tan yazar" },
              { icon: <TrendingUp className="h-4 w-4" />, t: "Reklam tıklaması boşa gitmez" },
            ].map(b => <BenefitCard key={b.t} {...b} />)}
          </div>
        </div>
      </section>

      {/* ================= PACKAGE ================= */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 py-10 md:py-16">
        <div className="ts-card rounded-[32px] p-6 md:p-10 relative overflow-hidden">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white" style={{ background: "var(--ts-yellow)", color: "#5B4200" }}>
                Her Şey Dahil
              </div>
              <h2 className="mt-3 text-[28px] md:text-4xl font-extrabold text-[var(--ts-navy)] leading-tight">Tek Paket, Sürpriz Masraf Yok</h2>
              <div className="mt-3 text-[15px] text-[var(--ts-text-2)]">Tesisatçı Web Sitesi Paketi</div>
              <div className="mt-4 text-[52px] md:text-6xl font-black text-[var(--ts-navy)] leading-none">13.000 <span className="text-3xl md:text-4xl" style={{ color: "var(--ts-blue)" }}>TL</span></div>
              <div className="mt-2 text-[13.5px] text-[var(--ts-text-2)]">5 Sayfa + Domain + 1 Yıl Hosting + SSL</div>
            </div>
            <ul className="grid gap-2.5">
              {included.map(item => (
                <li key={item} className="flex items-start gap-2.5 text-[14.5px]">
                  <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full text-white shrink-0" style={{ background: "var(--ts-turq)" }}>
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[var(--ts-navy)] font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-8">
            <WaButton
              label="Paketi WhatsApp'tan Başlat"
              message={waMessages.package}
              placement="package"
              size="lg"
              className="w-full text-lg"
            />
            <p className="mt-3 text-center text-[12.5px] text-[var(--ts-text-2)]">Ödeme ve firma bilgilerinin eksiksiz teslim edilmesinden sonra çalışma başlar.</p>
          </div>
        </div>
      </section>

      {/* ================= 3 STEPS ================= */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10 md:py-16">
        <h2 className="text-center text-[26px] md:text-4xl font-extrabold text-[var(--ts-navy)]">3 Kolay Adımda Hazır</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-4 relative">
          {[
            { n: 1, icon: <Briefcase className="h-6 w-6" />, t: "Beğendiğiniz örneği seçin" },
            { n: 2, icon: <FileText className="h-6 w-6" />, t: "Firma bilgilerinizi gönderin" },
            { n: 3, icon: <Rocket className="h-6 w-6" />, t: "Sitenizi kontrol edip yayına alın" },
          ].map(s => (
            <div key={s.n} className="ts-card rounded-3xl p-5 text-center">
              <div className="mx-auto grid place-items-center w-9 h-9 rounded-full text-white text-sm font-bold" style={{ background: "var(--ts-blue-dark)" }}>{s.n}</div>
              <div className="mt-4 mx-auto grid place-items-center w-16 h-16 rounded-2xl text-[var(--ts-blue-dark)]" style={{ background: "var(--ts-ice)" }}>
                {s.icon}
              </div>
              <div className="mt-4 font-bold text-[var(--ts-navy)]">{s.t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= ADS + SEO READY ================= */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10 md:py-16">
        <div className="ts-card rounded-[28px] p-6 md:p-8">
          <h2 className="text-[24px] md:text-3xl font-extrabold text-[var(--ts-navy)]">Google Reklamına ve SEO'ya Hazır</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            <ul className="grid gap-3">
              {["Mobilde hızlı açılır", "Reklamdan geleni WhatsApp'a yönlendirir", "Google'ın anlayabileceği düzenli sayfa yapısı"].map(t => (
                <li key={t} className="flex items-start gap-2.5">
                  <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full text-white shrink-0" style={{ background: "var(--ts-blue)" }}>
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[14.5px] text-[var(--ts-navy)] font-medium">{t}</span>
                </li>
              ))}
            </ul>
            {/* SERP mockup */}
            <div className="rounded-2xl border border-[var(--ts-border)] p-3 bg-white text-[12px]">
              <div className="text-[10px] text-slate-400 mb-1">Google</div>
              <div className="rounded-full border border-slate-200 px-3 py-1.5 text-slate-500">tesisatçı acil servis 🔍</div>
              <div className="mt-3 text-[11px]">
                <div className="font-semibold text-blue-700">Acil Tesisat | 7/24 Hizmet</div>
                <div className="text-slate-500 text-[10px]">www.acil-tesisat.com</div>
                <div className="text-slate-600 mt-1">Su kaçağı, tıkanıklık, tesisat tamiri.</div>
              </div>
              <div className="mt-2">
                <span className="inline-block rounded-full text-white text-[10px] font-bold px-2.5 py-1" style={{ background: "var(--ts-wa)" }}>WhatsApp'tan Yaz</span>
              </div>
            </div>
            {/* stats mockup */}
            <div className="rounded-2xl border border-[var(--ts-border)] p-3 bg-white">
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-semibold text-[var(--ts-navy)]">Site Performansı</span>
                <span className="rounded-full px-2 py-0.5 text-[9px] font-bold" style={{ background: "var(--ts-ice)", color: "var(--ts-blue-dark)" }}>Örnek Ölçüm Paneli</span>
              </div>
              <svg viewBox="0 0 120 50" className="w-full mt-2">
                <polyline points="0,42 20,36 40,30 60,24 80,18 100,10 120,6" fill="none" stroke="#16C7B7" strokeWidth="2.5" strokeLinecap="round"/>
                <polyline points="0,42 20,36 40,30 60,24 80,18 100,10 120,6" fill="url(#g1)" opacity=".15" />
                <defs><linearGradient id="g1" x1="0" x2="0" y1="0" y2="1"><stop stopColor="#16C7B7" offset="0"/><stop stopColor="#fff" offset="1"/></linearGradient></defs>
              </svg>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="rounded-xl p-2 text-center" style={{ background: "var(--ts-ice)" }}>
                  <div className="text-[10px] text-[var(--ts-text-2)]">Tıklamalar</div>
                  <div className="font-bold text-[var(--ts-navy)]">Örnek</div>
                </div>
                <div className="rounded-xl p-2 text-center text-white" style={{ background: "var(--ts-wa)" }}>
                  <div className="text-[10px] opacity-90">WhatsApp</div>
                  <div className="font-bold">Örnek</div>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-5 text-[12.5px] text-[var(--ts-text-2)] italic">Sıralama veya müşteri garantisi verilmez.</p>
        </div>
      </section>

      {/* ================= TRUST CARDS ================= */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-6 md:py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: <Clock className="h-5 w-5" />, t: "2007'den beri deneyim" },
            { icon: <Award className="h-5 w-5" />, t: "Gerçek çalışma örnekleri" },
            { icon: <ShieldCheck className="h-5 w-5" />, t: "Şeffaf paket" },
            { icon: <Rocket className="h-5 w-5" />, t: "Açık teslim süreci" },
          ].map(k => (
            <div key={k.t} className="ts-card rounded-2xl p-4 text-center">
              <div className="mx-auto grid place-items-center w-11 h-11 rounded-2xl text-white" style={{ background: "linear-gradient(135deg,var(--ts-blue),var(--ts-turq))" }}>
                {k.icon}
              </div>
              <div className="mt-3 text-[13.5px] font-bold text-[var(--ts-navy)]">{k.t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="mx-auto max-w-4xl px-4 sm:px-6 py-10 md:py-16">
        <h2 className="text-[26px] md:text-4xl font-extrabold text-[var(--ts-navy)] text-center">Sık Sorulan Sorular</h2>
        <div className="mt-8 grid gap-3">
          {faqs.map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={f.q} className="ts-card rounded-2xl overflow-hidden">
                <button
                  className="w-full text-left px-4 py-4 flex items-center justify-between gap-3"
                  aria-expanded={open}
                  aria-controls={`faq-${i}`}
                  onClick={() => { const next = open ? null : i; setOpenFaq(next); if (!open) track("faq_open", { question: f.q }); }}
                  style={{ minHeight: 56 }}
                >
                  <span className="font-semibold text-[15px] text-[var(--ts-navy)]">{f.q}</span>
                  <span className={`shrink-0 grid place-items-center w-7 h-7 rounded-full text-white transition-transform ${open ? "rotate-45" : ""}`} style={{ background: "var(--ts-blue)" }}>
                    <span className="text-lg leading-none">+</span>
                  </span>
                </button>
                <div id={`faq-${i}`} hidden={!open} className="px-4 pb-4 text-[14px] text-[var(--ts-text-2)] leading-relaxed">
                  {f.a}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="px-4 sm:px-6 py-10 md:py-16">
        <div className="mx-auto max-w-5xl rounded-[32px] p-6 md:p-10 relative overflow-hidden text-white"
             style={{ background: "linear-gradient(135deg,#149FE8 0%,#087EC0 100%)" }}>
          <WaterDropsBackground variant="section" density="high" />
          <Mascot size={90} alt="" className="absolute -bottom-2 -left-2 opacity-90 hidden sm:block z-10" />
          <div className="relative grid md:grid-cols-[1fr_auto] items-center gap-6">
            <div className="md:pl-24">
              <h2 className="text-[26px] md:text-4xl font-extrabold leading-tight">Sıradaki Web Sitesi<br/>Sizin Olsun</h2>
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/25 px-3 py-1.5 text-[13px] font-bold">
                Her Şey Dahil <span className="text-[var(--ts-yellow)]">13.000 TL</span>
              </div>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3">
              <WaButton
                label="WhatsApp'tan Hemen Yaz"
                message={waMessages.final}
                placement="final_cta"
                size="lg"
                className="w-full md:w-auto"
              />
              <div className="flex items-center gap-2.5 text-[13px] text-white/90">
                <img src={profilPortrait} alt="Dijital Mustafa" width={44} height={44} loading="lazy" className="w-11 h-11 rounded-full object-cover object-top border-2 border-white/70" />
                <span>Sorunuzu doğrudan bana yazabilirsiniz.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="border-t border-[var(--ts-soft)] bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 md:py-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-3">
              <Mascot size={40} alt="" />
              <div>
                <div className="font-bold text-[var(--ts-navy)]">Dijital Mustafa</div>
                <div className="text-[12.5px] text-[var(--ts-text-2)] flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{ADDRESS} · Beylikdüzü</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-[13px]">
              <a href="/gizlilik-politikasi" className="text-[var(--ts-text-2)] hover:text-[var(--ts-blue-dark)]">Gizlilik Politikası</a>
              <a href="/kvkk" className="text-[var(--ts-text-2)] hover:text-[var(--ts-blue-dark)]">KVKK</a>
              <a href="/cerez-politikasi" className="text-[var(--ts-text-2)] hover:text-[var(--ts-blue-dark)]">Çerez Politikası</a>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-[var(--ts-soft)] text-center text-[12.5px] text-[var(--ts-text-2)]">
            © {new Date().getFullYear()} Dijital Mustafa · Tesisatçılara Özel Web Sitesi
          </div>
        </div>
        <div className="h-20 md:h-0" /> {/* spacer for sticky bar on mobile */}
      </footer>

      {/* ================= STICKY MOBILE WA ================= */}
      <div className="md:hidden fixed inset-x-0 bottom-0 z-50 safe-bottom px-3 pt-2" style={{ background: "linear-gradient(180deg,transparent,rgba(255,255,255,0.95) 40%)" }}>
        <a
          href={waLink(waMessages.sticky)}
          target="_blank" rel="noopener noreferrer"
          onClick={() => trackWA("sticky_mobile")}
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl text-white font-bold text-[15px] shadow-2xl"
          style={{ background: "linear-gradient(180deg,#2BD46E,#1EAE54)", minHeight: 52 }}
          aria-label="WhatsApp'tan Bilgi Al"
        >
          <MessageCircle className="h-5 w-5" /> WhatsApp'tan Bilgi Al
        </a>
      </div>
    </div>
  );
}

// ================= sub-components =================
function PortfolioCard({ p }: { p: (typeof portfolio)[number] }) {
  return (
    <div
      className="ts-card rounded-3xl overflow-hidden flex flex-col"
      onMouseEnter={() => track("portfolio_view", { project_name: p.name })}
    >
      <div className="aspect-[4/3] bg-[var(--ts-ice)] overflow-hidden border-b border-[var(--ts-soft)]">
        <img src={p.image} alt={`${p.name} web sitesi ekran görüntüsü`} width={520} height={390} loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />
      </div>
      <div className="p-4 flex flex-col flex-1 gap-3">
        <div>
          <div className="font-bold text-[var(--ts-navy)]">{p.name}</div>
          <div className="mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold" style={{ background: "var(--ts-ice)", color: "var(--ts-blue-dark)" }}>{p.badge}</div>
        </div>
        <div className="grid gap-2 mt-auto">
          <a
            href={p.url}
            target="_blank" rel="noopener noreferrer nofollow"
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[var(--ts-border)] px-3 py-2 text-[13px] font-semibold text-[var(--ts-navy)] hover:bg-[var(--ts-ice)]"
            style={{ minHeight: 44 }}
          >
            Canlı Siteyi İncele <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <a
            href={waLink(waMessages.portfolio(p.name))}
            target="_blank" rel="noopener noreferrer"
            onClick={() => trackWA(`portfolio_${p.name.replace(/\s+/g, "_").toLowerCase()}`)}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl text-white font-semibold text-[13px]"
            style={{ background: "var(--ts-wa)", minHeight: 44 }}
          >
            <MessageCircle className="h-4 w-4" /> Benzerini İstiyorum
          </a>
        </div>
      </div>
    </div>
  );
}

function BenefitCard({ icon, t }: { icon: React.ReactNode; t: string }) {
  return (
    <div className="ts-card rounded-2xl p-3.5 flex items-center gap-2.5">
      <span className="grid place-items-center h-9 w-9 rounded-xl text-white shrink-0" style={{ background: "linear-gradient(135deg,var(--ts-blue),var(--ts-turq))" }}>
        {icon}
      </span>
      <span className="text-[13.5px] font-semibold text-[var(--ts-navy)]">{t}</span>
    </div>
  );
}

function PhoneMockup() {
  return (
    <div className="w-[220px] rounded-[38px] p-2 shadow-2xl" style={{ background: "var(--ts-navy)" }}>
      <div className="rounded-[30px] bg-white overflow-hidden">
        <div className="h-7 grid place-items-center" style={{ background: "var(--ts-blue-dark)" }}>
          <div className="w-16 h-1.5 rounded-full bg-white/30" />
        </div>
        <div className="p-3">
          <div className="rounded-xl p-3 text-white text-[11px] font-bold" style={{ background: "linear-gradient(135deg,var(--ts-blue),var(--ts-blue-dark))" }}>
            Tesisat Hizmeti<br/><span className="opacity-90 font-medium">7/24 Yanınızdayız</span>
          </div>
          <div className="mt-2 flex gap-1.5">
            <div className="h-8 flex-1 rounded-lg" style={{ background: "var(--ts-ice)" }} />
            <div className="h-8 flex-1 rounded-lg" style={{ background: "var(--ts-ice)" }} />
          </div>
          <div className="mt-2 space-y-1">
            <div className="h-1.5 rounded bg-slate-100" />
            <div className="h-1.5 rounded bg-slate-100 w-4/5" />
            <div className="h-1.5 rounded bg-slate-100 w-2/3" />
          </div>
          <div className="mt-3 h-9 rounded-xl grid place-items-center text-white text-[11px] font-bold" style={{ background: "var(--ts-wa)" }}>
            <span className="inline-flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp'tan Yaz</span>
          </div>
        </div>
      </div>
    </div>
  );
}
